// src/Components/Modules/CRM/CrmLeads.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Skeleton,
  Stack, Avatar, Divider, LinearProgress, Grid
} from "@mui/material";
import {
  FaPlus, FaEdit, FaTrash, FaHandshake, FaSync, FaFlag, FaArrowRight
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const BoardContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: theme.spacing(2),
}));

const KanbanColumn = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  background: "#f8f9fa",
  border: `2px solid ${color}20`,
  minHeight: 400,
}));

const DealCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  background: "white",
  borderLeft: "4px solid #667eea",
  marginBottom: theme.spacing(1.5),
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
}));

const CrmDeals = () => {
  const [pipeline, setPipeline] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [form, setForm] = useState({
    title: "", customer_id: "", stage_id: "", assigned_to: "",
    value: 0, probability: 0, description: "", expected_close_date: ""
  });
  const [customers, setCustomers] = useState([]); // You might need to fetch this
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("crm/deals");
      setPipeline(res.data.pipeline || []);
      setStats(res.data.stats || {});
      // Fetch customers and users if needed
      const usersRes = await AxiosAuthInstance.get("hr/projects/users/assignable");
      setUsers(usersRes.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to load deals", "error");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (deal = null, defaultStageId = null) => {
    if (deal) {
      setEditingDeal(deal);
      setForm({
        title: deal.title, customer_id: deal.customer_id || "", stage_id: deal.stage_id || "",
        assigned_to: deal.assigned_to || "", value: deal.value || 0, probability: deal.probability || 0,
        description: deal.description || "", expected_close_date: deal.expected_close_date || ""
      });
    } else {
      setEditingDeal(null);
      setForm({ title: "", customer_id: "", stage_id: defaultStageId || "", assigned_to: "", value: 0, probability: 0, description: "", expected_close_date: "" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      Swal.fire("Warning", "Deal title is required", "warning");
      return;
    }
    try {
      if (editingDeal) {
        await AxiosAuthInstance.put(`crm/deals/${editingDeal.id}`, form);
        Swal.fire({ icon: "success", title: "Updated!", timer: 1500, showConfirmButton: false });
      } else {
        await AxiosAuthInstance.post("crm/deals", form);
        Swal.fire({ icon: "success", title: "Created!", timer: 1500, showConfirmButton: false });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const handleMove = async (deal, newStageId) => {
    try {
      await AxiosAuthInstance.post(`crm/deals/${deal.id}/move`, { stage_id: newStageId });
      fetchData();
    } catch (err) {
      Swal.fire("Error", "Failed to move deal", "error");
    }
  };

  const handleDelete = async (deal) => {
    const result = await Swal.fire({
      title: "Delete Deal?", text: `"${deal.title}" will be permanently deleted.`,
      icon: "warning", showCancelButton: true, confirmButtonColor: "#f44336", confirmButtonText: "Yes, Delete",
    });
    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`crm/deals/${deal.id}`);
        Swal.fire("Deleted!", "Deal deleted", "success");
        fetchData();
      } catch (err) {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const formatCurrency = (value) => value ? "৳" + Number(value).toLocaleString("en-BD") : "৳0";

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="CRM Deals" />

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom><FaHandshake style={{ marginRight: 10, verticalAlign: "middle" }} />Deals Pipeline</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Manage your sales opportunities</Typography>
          </Box>
          <Button variant="contained" startIcon={<FaPlus />} onClick={() => openDialog()} sx={{ bgcolor: "white", color: "#667eea", fontWeight: 600, "&:hover": { bgcolor: "#f0f0f0" }, textTransform: "none" }}>New Deal</Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
          {[
            { label: "Open Deals", value: stats.open || 0 },
            { label: "Pipeline Value", value: formatCurrency(stats.total_value) },
            { label: "Weighted Value", value: formatCurrency(stats.weighted_value) },
            { label: "Won Revenue", value: formatCurrency(stats.won_value) },
          ].map((s, i) => (
            <Paper key={i} sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.15)", color: "white", borderRadius: 2, minWidth: 120, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.label}</Typography>
              <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
            </Paper>
          ))}
        </Box>
      </Paper>

      {loading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      ) : (
        <BoardContainer>
          {pipeline.map((col) => (
            <KanbanColumn key={col.stage.id} color={col.stage.color} elevation={0}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, pb: 1, borderBottom: `2px solid ${col.stage.color}` }}>
                <Typography variant="subtitle1" fontWeight={700}>{col.stage.name}</Typography>
                <Chip label={col.count} size="small" sx={{ ml: "auto", bgcolor: col.stage.color, color: "white", fontWeight: 700 }} />
              </Box>

              {col.deals.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No deals</Typography>
                  <Button size="small" startIcon={<FaPlus />} onClick={() => openDialog(null, col.stage.id)} sx={{ color: col.stage.color, textTransform: "none" }}>Add Deal</Button>
                </Box>
              ) : (
                col.deals.map((deal) => (
                  <DealCard key={deal.id} elevation={0}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, pr: 1 }}>{deal.title}</Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(deal)} sx={{ p: 0.5 }}><FaEdit size={11} color="#ff9800" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(deal)} sx={{ p: 0.5 }}><FaTrash size={11} color="#f44336" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight={700} color="success.main">{formatCurrency(deal.value)}</Typography>
                    <Typography variant="caption" color="text.secondary">{deal.customer?.name || "No Customer"}</Typography>
                    
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={deal.probability} sx={{ height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: col.stage.color } }} />
                      <Typography variant="caption" color="text.secondary">{deal.probability}% Probability</Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.5, mt: 1, pt: 1, borderTop: "1px solid #f0f0f0", flexWrap: "wrap" }}>
                      {pipeline.filter(c => c.stage.id !== col.stage.id).map((c) => (
                        <Tooltip key={c.stage.id} title={`Move to ${c.stage.name}`}>
                          <IconButton size="small" onClick={() => handleMove(deal, c.stage.id)} sx={{ p: 0.3, color: c.stage.color }}><FaArrowRight size={12} /></IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  </DealCard>
                ))
              )}
            </KanbanColumn>
          ))}
        </BoardContainer>
      )}

      {/* Deal Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>{editingDeal ? "Edit Deal" : "Create New Deal"}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField fullWidth label="Deal Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={2} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Stage</InputLabel>
                  <Select value={form.stage_id} label="Stage" onChange={(e) => setForm({ ...form, stage_id: e.target.value })}>
                    {pipeline.map(p => <MenuItem key={p.stage.id} value={p.stage.id}>{p.stage.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select value={form.assigned_to} label="Assign To" onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Probability (%)" type="number" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} inputProps={{ min: 0, max: 100 }} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Expected Close Date" type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: "#667eea" }}>{editingDeal ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmDeals;